import { useInscalar } from '@ilg-core/inscalar-web/react'
import { useCallback } from 'react'

import {
    getExperimentIdsForEvent,
    mapCardAltProductToInscalarTrackedProduct,
    mapCardScoredProductsToInscalarTrackedProducts,
    mapCardScoredProductToInscalarTrackedProductForClickout,
} from '@ilg-totallymoney/cardloanutils'
import {
    InscalarEventBase,
    InscalarEventType,
    InscalarTrackPayload,
} from '@ilg-totallymoney/types'
import Cookies from 'js-cookie'

import {
    InscalarEventAction,
    InscalarEventClickout,
    InscalarEventClickoutAlt,
    InscalarEventPageView,
    InscalarEventPageViewAlt,
} from '@appTypes/InscalarEvents.interface'

import useAppQueryState from '@hooks/useAppQueryState'

import { useMinimumGuaranteedCreditLimitQuery } from '@queries/useMinimumGuaranteedCreditLimitQuery'

import { getInscalarAppEnv } from '@utils/getInscalarAppEnv'
import { inscalarTracker } from '@utils/inscalar'

import useFeatureFlags from './useFeatureFlags'

export const COOKIE_CONSENT_KEY = 'cookieyes-consent'

const useInscalarTrackEvent = () => {
    const { featureFlags } = useFeatureFlags()
    const { track } = useInscalar()
    const { appQueryState } = useAppQueryState()
    const { data: minimumCreditLimit } = useMinimumGuaranteedCreditLimitQuery({
        customerId: appQueryState.customerId,
    })

    const trackBase = useCallback(
        (eventType: InscalarEventType, data?: InscalarEventBase) => {
            const outcome = data?.postSubmit?.outcome

            const previousStep = data?.step
                ? inscalarTracker.getPreviousStepForEvent(data.step)
                : null

            const originalJourney =
                data?.journey !== undefined &&
                data?.journeyCategory !== undefined
                    ? inscalarTracker.getOriginalJourneyForEvent(
                          data.journey,
                          data.journeyCategory
                      )
                    : undefined

            const payload: InscalarTrackPayload = {
                event_type: eventType,
                env: getInscalarAppEnv(),
                client: 'web',
                filters: {
                    cookie_consents:
                        Cookies.get(COOKIE_CONSENT_KEY)?.split(',') ?? [],
                    experiment_ids: getExperimentIdsForEvent(featureFlags),
                },

                journey: data?.journey,
                journey_category: data?.journeyCategory,

                original_journey: originalJourney?.journey,
                original_journey_category: originalJourney?.journeyCategory,

                step: data?.step,
                previous_step: previousStep,

                post_submit: {
                    outcome: {
                        credit_limit_requested: outcome?.creditLimitRequested,
                        results: outcome?.results,
                        results_displayed: outcome?.resultsDisplayed,
                        results_sorting: outcome?.resultsSorting,
                    },
                    product_click: data?.postSubmit?.productClick,
                },
            }

            track(
                'frontend_event',
                '10.4',
                {},
                payload as unknown as Record<string, unknown>
            )
        },
        [featureFlags, track]
    )

    const trackPageView = useCallback(
        ({
            step,
            journey,
            journeyCategory,
            products,
            resultsSorting,
            highlightProduct,
            numberOfCardsToShow,
        }: InscalarEventPageView) => {
            const resultsDisplayed =
                mapCardScoredProductsToInscalarTrackedProducts({
                    products,
                    highlightProduct,
                    numberOfCardsToShow,
                })

            trackBase('pageview', {
                journey,
                journeyCategory,
                step,
                postSubmit: {
                    outcome: {
                        creditLimitRequested:
                            minimumCreditLimit?.minimumGuaranteedCreditLimit!,
                        resultsDisplayed,
                        resultsSorting: resultsSorting,
                        results: resultsDisplayed.length,
                    },
                },
            })
        },
        [minimumCreditLimit?.minimumGuaranteedCreditLimit, trackBase]
    )

    const trackPageViewAlt = useCallback(
        ({
            step,
            journey,
            journeyCategory,
            products,
        }: InscalarEventPageViewAlt) => {
            const resultsDisplayed = products?.map((product, index) =>
                mapCardAltProductToInscalarTrackedProduct({ product, index })
            )

            trackBase('pageview', {
                journey,
                journeyCategory,
                step,
                postSubmit: {
                    outcome: {
                        creditLimitRequested:
                            minimumCreditLimit?.minimumGuaranteedCreditLimit!,
                        resultsDisplayed: resultsDisplayed,
                        results: resultsDisplayed.length,
                        resultsSorting: undefined,
                    },
                },
            })
        },
        [minimumCreditLimit?.minimumGuaranteedCreditLimit, trackBase]
    )

    const trackAccept = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards' | 'personal_loans'
            journeyCategory: 'credit_cards' | 'loans'
            step: 'credit_cards_scoring' | 'personal_loans_scoring'
        }) => {
            trackBase('accept', { journey, journeyCategory, step })
        },
        [trackBase]
    )

    const trackStart = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards'
            journeyCategory: 'credit_cards'
            step: 'credit_limit_request'
        }) => {
            trackBase('start', { step, journey, journeyCategory })
        },
        [trackBase]
    )

    const trackSubmit = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards'
            journeyCategory: 'credit_cards'
            step: 'credit_cards_search' | 'credit_limit_request'
        }) => {
            trackBase('submit', { journey, journeyCategory, step })
        },
        [trackBase]
    )

    const trackDecline = useCallback(
        ({
            step,
            journey,
            journeyCategory,
        }: {
            journey: 'credit_cards' | 'personal_loans'
            journeyCategory: 'credit_cards' | 'loans'
            step: 'credit_cards_scoring' | 'personal_loans_scoring'
        }) => {
            trackBase('decline', { journey, journeyCategory, step })
        },
        [trackBase]
    )

    const trackAction = useCallback(
        ({ step, journey, journeyCategory }: InscalarEventAction) => {
            trackBase('action', { journey, journeyCategory, step })
        },
        [trackBase]
    )

    const trackClickoutAlt = useCallback(
        ({
            step,
            journey,
            journeyCategory,
            product,
            productPosition,
        }: InscalarEventClickoutAlt) => {
            trackBase('clickout', {
                journey,
                journeyCategory,
                step,
                postSubmit: {
                    productClick: mapCardAltProductToInscalarTrackedProduct({
                        product,
                        index: productPosition ?? 0,
                    }),
                },
            })
        },
        [trackBase]
    )

    const trackClickout = useCallback(
        ({
            step,
            journey,
            journeyCategory,
            product,
            allProducts,
            productPosition,
            clickId,
        }: InscalarEventClickout) => {
            trackBase('clickout', {
                journey,
                journeyCategory,
                step,
                postSubmit: {
                    productClick:
                        mapCardScoredProductToInscalarTrackedProductForClickout(
                            {
                                product,
                                allProducts,
                                index: productPosition ?? 0,
                                clickId,
                            }
                        ),
                },
            })
        },
        [trackBase]
    )

    return {
        trackBase,
        trackPageViewAlt,
        trackPageView,
        trackAccept,
        trackStart,
        trackSubmit,
        trackDecline,
        trackAction,
        trackClickout,
        trackClickoutAlt,
    }
}

export default useInscalarTrackEvent
